import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/loomi/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Shortcode from "@/pages/Shortcode";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminProjectDetail from "@/pages/admin/AdminProjectDetail";
import AdminClients from "@/pages/admin/AdminClients";
import AdminRespondents from "@/pages/admin/AdminRespondents";
import AdminLogs from "@/pages/admin/AdminLogs";
import AdminRewards from "@/pages/admin/AdminRewards";
import AdminBulkUpload from "@/pages/admin/AdminBulkUpload";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminVendors from "@/pages/admin/AdminVendors";
import AdminVendorDetail from "@/pages/admin/AdminVendorDetail";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminPartnerDetail from "@/pages/admin/AdminPartnerDetail";

import PmDashboard from "@/pages/pm/PmDashboard";
import PmCreate from "@/pages/pm/PmCreate";
import PmProjectDetail from "@/pages/pm/PmProjectDetail";
import PmSettings from "@/pages/pm/PmSettings";

import PanelDashboard from "@/pages/panel/PanelDashboard";
import PanelHistory from "@/pages/panel/PanelHistory";
import PanelProfile from "@/pages/panel/PanelProfile";
import PanelRewards from "@/pages/panel/PanelRewards";
import PanelAuthCallback from "@/pages/panel/PanelAuthCallback";

import ClientDashboard from "@/pages/client/ClientDashboard";
import ClientProjectDetail from "@/pages/client/ClientProjectDetail";

import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VendorDocs from "@/pages/VendorDocs";
import OperatorProfile from "@/pages/OperatorProfile";

function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-mute">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  const map = { admin: "/admin", pm: "/pm", panel: "/panel", client: "/client" };
  return <Navigate to={map[user.role] || "/"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" toastOptions={{ classNames: { toast: "glass" } }} />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login variant="admin" />} />
          <Route path="/pm/login" element={<Login variant="pm" />} />
          <Route path="/panel/login" element={<Login variant="panel" />} />
          <Route path="/client/login" element={<Login variant="client" />} />
          <Route path="/panel/register" element={<Register />} />
          <Route path="/panel/auth/callback" element={<PanelAuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/s/:code" element={<Shortcode />} />
          <Route path="/docs/vendor" element={<VendorDocs />} />
          <Route path="/home" element={<RoleHome />} />

          <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/projects" element={<ProtectedRoute roles={["admin"]}><AdminProjects /></ProtectedRoute>} />
          <Route path="/admin/projects/:pid" element={<ProtectedRoute roles={["admin"]}><AdminProjectDetail /></ProtectedRoute>} />
          <Route path="/admin/clients" element={<ProtectedRoute roles={["admin"]}><AdminClients /></ProtectedRoute>} />
          <Route path="/admin/respondents" element={<ProtectedRoute roles={["admin"]}><AdminRespondents /></ProtectedRoute>} />
          <Route path="/admin/logs" element={<ProtectedRoute roles={["admin"]}><AdminLogs /></ProtectedRoute>} />
          <Route path="/admin/rewards" element={<ProtectedRoute roles={["admin"]}><AdminRewards /></ProtectedRoute>} />
          <Route path="/admin/bulk-upload" element={<ProtectedRoute roles={["admin"]}><AdminBulkUpload /></ProtectedRoute>} />
          <Route path="/admin/vendors" element={<ProtectedRoute roles={["admin"]}><AdminVendors /></ProtectedRoute>} />
          <Route path="/admin/vendors/:vid" element={<ProtectedRoute roles={["admin"]}><AdminVendorDetail /></ProtectedRoute>} />
          <Route path="/admin/partners" element={<ProtectedRoute roles={["admin"]}><AdminPartners /></ProtectedRoute>} />
          <Route path="/admin/partners/:pid" element={<ProtectedRoute roles={["admin"]}><AdminPartnerDetail /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><AdminSettings /></ProtectedRoute>} />
          <Route path="/admin/profile" element={<ProtectedRoute roles={["admin"]}><OperatorProfile role="admin" /></ProtectedRoute>} />

          <Route path="/pm" element={<ProtectedRoute roles={["pm","admin"]} redirectTo="/pm/login"><PmDashboard /></ProtectedRoute>} />
          <Route path="/pm/projects" element={<ProtectedRoute roles={["pm","admin"]} redirectTo="/pm/login"><PmDashboard /></ProtectedRoute>} />
          <Route path="/pm/projects/:pid" element={<ProtectedRoute roles={["pm","admin"]} redirectTo="/pm/login"><PmProjectDetail /></ProtectedRoute>} />
          <Route path="/pm/new" element={<ProtectedRoute roles={["pm","admin"]} redirectTo="/pm/login"><PmCreate /></ProtectedRoute>} />
          <Route path="/pm/settings" element={<ProtectedRoute roles={["pm"]} redirectTo="/pm/login"><PmSettings /></ProtectedRoute>} />
          <Route path="/pm/profile" element={<ProtectedRoute roles={["pm"]} redirectTo="/pm/login"><OperatorProfile role="pm" /></ProtectedRoute>} />

          <Route path="/panel" element={<ProtectedRoute roles={["panel"]} redirectTo="/panel/login"><PanelDashboard /></ProtectedRoute>} />
          <Route path="/panel/history" element={<ProtectedRoute roles={["panel"]} redirectTo="/panel/login"><PanelHistory /></ProtectedRoute>} />
          <Route path="/panel/profile" element={<ProtectedRoute roles={["panel"]} redirectTo="/panel/login"><PanelProfile /></ProtectedRoute>} />
          <Route path="/panel/rewards" element={<ProtectedRoute roles={["panel"]} redirectTo="/panel/login"><PanelRewards /></ProtectedRoute>} />

          <Route path="/client" element={<ProtectedRoute roles={["client"]} redirectTo="/client/login"><ClientDashboard /></ProtectedRoute>} />
          <Route path="/client/projects" element={<ProtectedRoute roles={["client"]} redirectTo="/client/login"><ClientDashboard /></ProtectedRoute>} />
          <Route path="/client/projects/:pid" element={<ProtectedRoute roles={["client"]} redirectTo="/client/login"><ClientProjectDetail /></ProtectedRoute>} />
          <Route path="/client/profile" element={<ProtectedRoute roles={["client"]} redirectTo="/client/login"><OperatorProfile role="client" /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
